import { AgendaConfiguration, WeeklySchedule, DayWithHours, Hour } from '@/api/agendaConfiguration';
import { Booking } from '@/api/booking';
import { GoogleCalendarEvent } from '@/api/users';

import { startOfWeek, addDays, Day, parseISO, formatISO } from 'date-fns';
import { toZonedTime, format, getTimezoneOffset, fromZonedTime } from 'date-fns-tz';
import { cloneDeep } from 'lodash';

export type TimeRange = { start: string; end: string };

export const weekDays = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export const isValidTimeRange = (timeRange: TimeRange): boolean => {
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/; // Formato HH:mm válido

  const { start, end } = timeRange;

  if (!start || !end) {
    return false;
  }

  if (!timeRegex.test(start) || !timeRegex.test(end)) {
    return false;
  }

  const [startHours, startMinutes] = start.split(':').map(Number);
  const [endHours, endMinutes] = (end === "00:00" ? "23:59" : end).split(':').map(Number);

  const startInMinutes = startHours * 60 + startMinutes;
  const endInMinutes = endHours * 60 + endMinutes;

  return startInMinutes < endInMinutes;
};

export function convertTo24HourFormat(time: string): string {
  const amPmMatch = time.match(/(\d{1,2}):(\d{2})(am|pm)/i);

  if (!amPmMatch) {
    throw new Error('Invalid time format. Expected format: hh:mmam or hh:mmpm');
  }

  let [_, hour, minute, period] = amPmMatch;
  let hourIn24Format = parseInt(hour, 10);

  if (period.toLowerCase() === 'pm' && hourIn24Format !== 12) {
    hourIn24Format += 12; // Convert PM hour to 24-hour format
  } else if (period.toLowerCase() === 'am' && hourIn24Format === 12) {
    hourIn24Format = 0; // Midnight in 24-hour format
  }

  return `${hourIn24Format.toString().padStart(2, '0')}:${minute}`;
}

export function orderAmPmHours(hours: string[]) {
  const uniqueHours = Array.from(new Set(hours));

  return uniqueHours.sort((a, b) => {
    const [hourA, minutesA] = convertTo24HourFormat(a).split(':').map(Number);
    const [hourB, minutesB] = convertTo24HourFormat(b).split(':').map(Number);
    return hourA * 60 + minutesA - (hourB * 60 + minutesB);
  });
}

export const parseTime = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

export const formatTime = (timeInMinutes: number): string => {
  const hours = Math.floor(timeInMinutes / 60);
  const minutes = timeInMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export const getRemainingTimeSlots = (
  available: TimeRange[],
  occupied: TimeRange[]
): TimeRange[] => {
  const validAvailable = available.filter(isValidTimeRange);
  const validOccupied = occupied.filter(isValidTimeRange);

  let remainingSlots: TimeRange[] = [...validAvailable];

  validOccupied.forEach((occupiedRange) => {
    const [occupiedStart, occupiedEnd] = [
      parseTime(occupiedRange.start),
      parseTime(occupiedRange.end === "00:00" ? "23:59" : occupiedRange.end),
    ];

    remainingSlots = remainingSlots.flatMap((slot) => {
      const [slotStart, slotEnd] = [parseTime(slot.start), parseTime(slot.end === "00:00" ? "23:59" : slot.end)];

      if (occupiedEnd <= slotStart || occupiedStart >= slotEnd) {
        // No hay intersección, el slot permanece igual
        return [slot];
      } else if (occupiedStart > slotStart && occupiedEnd < slotEnd) {
        // El rango ocupado divide el slot disponible en dos
        return [
          { start: formatTime(slotStart), end: formatTime(occupiedStart) === "23:59" ? "00:00" : formatTime(occupiedStart) },
          { start: formatTime(occupiedEnd), end: formatTime(slotEnd) === "23:59" ? "00:00" : formatTime(slotEnd) },
        ];
      } else if (occupiedStart <= slotStart && occupiedEnd < slotEnd) {
        // El rango ocupado elimina la parte inicial del slot
        return [{ start: formatTime(occupiedEnd), end: formatTime(slotEnd) === "23:59" ? "00:00" : formatTime(slotEnd) }];
      } else if (occupiedStart > slotStart && occupiedEnd >= slotEnd) {
        // El rango ocupado elimina la parte final del slot
        return [{ start: formatTime(slotStart), end: formatTime(occupiedStart) === "23:59" ? "00:00" : formatTime(occupiedStart) }];
      } else {
        // El rango ocupado cubre completamente el slot
        return [];
      }
    });
  });

  return remainingSlots;
};

export const isAvailable = (
  date: Date,
  agendaConfiguration: AgendaConfiguration,
  formattedBookings: DayWithHours[] = []
) => {
  let isAvailable = false;
  let availableHours: TimeRange[] = [];
  const targetDate = date.toISOString().split('T')[0];
  if (agendaConfiguration.specificDays && agendaConfiguration?.specificDays?.length > 0) {
    const index = agendaConfiguration.specificDays.findIndex(
      (item) => new Date(item.date).toISOString().split('T')[0] === targetDate
    );
    if (index > -1 && agendaConfiguration.specificDays[index].hours?.length > 0) {
      agendaConfiguration.specificDays[index].hours.forEach((hour) => {
        if (isValidTimeRange(hour)) {
          isAvailable = true;
        }
      });
      availableHours = [...agendaConfiguration.specificDays[index].hours];
    }
  }
  if (!isAvailable) {
    const dayOfTheWeek = date.getDay();
    if (
      agendaConfiguration.weeklySchedule[weekDays[dayOfTheWeek] as keyof WeeklySchedule]?.length > 0
    ) {
      agendaConfiguration.weeklySchedule[weekDays[dayOfTheWeek] as keyof WeeklySchedule].forEach(
        (hour) => {
          if (isValidTimeRange(hour)) {
            isAvailable = true;
          }
        }
      );
      availableHours = [
        ...agendaConfiguration.weeklySchedule[weekDays[dayOfTheWeek] as keyof WeeklySchedule],
      ];
    }
  }
  let busyDays: DayWithHours[] =
    agendaConfiguration.busyDays && agendaConfiguration?.busyDays?.length > 0
      ? agendaConfiguration?.busyDays
      : [];
  if (formattedBookings.length > 0) {
    busyDays = [...busyDays, ...formattedBookings];
  }
  if (isAvailable) {
    if (busyDays.length > 0) {
      const index = busyDays.findIndex(
        (item) => new Date(item.date).toISOString().split('T')[0] === targetDate
      );
      if (index > -1 && busyDays[index].hours?.length === 0) {
        isAvailable = false;
      }
      if (index > -1 && busyDays[index].hours?.length > 0) {
        const remainingSlots = getRemainingTimeSlots(availableHours, busyDays[index].hours);
        isAvailable = remainingSlots.length > 0;
      }
    }
  }
  return isAvailable;
};

export const formatToAmPm = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600) % 24;
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const period = hours >= 12 ? 'pm' : 'am';
  const adjustedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, '0');
  return `${adjustedHours}:${formattedMinutes}${period}`;
};

export function generateTimeIntervals(
  ranges: TimeRange[],
  incrementInSeconds: number,
  timeException: number,
  date: Date,
  dateTimeLimit: Date
): string[] {
  if (incrementInSeconds <= 0) throw "incremetInSeconds es 0 o negativo";
  function isDate2Bigger(date1: Date, seconds: number, date2: Date): boolean {
    const adjustedDate1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate(), 0, 0, 0);
    adjustedDate1.setSeconds(seconds);
    return date2.getTime() > adjustedDate1.getTime();
  }

  // 1. Encontrar el tiempo de fin más tardío en todos los rangos proporcionados.
  let globalEndInSeconds = 0;
  ranges.forEach((range) => {
    if (!isValidTimeRange(range)) return;
    const [endHours, endMinutes] = (range.end === "00:00" ? "23:59" : range.end).split(':').map(Number);
    const endInSeconds = endHours * 3600 + endMinutes * 60;
    if (endInSeconds > globalEndInSeconds) {
      globalEndInSeconds = endInSeconds;
    }
  });

  // Si no se encontraron rangos válidos, retornamos un array vacío.
  if (ranges.filter(isValidTimeRange).length === 0) return [];

  // 2. Generar todos los posibles intervalos desde el inicio del día (00:00:00) hasta el tiempo de fin global más tardío.
  const allPossibleIntervals: number[] = [];
  for(let currentInSeconds = 0; currentInSeconds <= globalEndInSeconds; currentInSeconds += incrementInSeconds) {
    allPossibleIntervals.push(currentInSeconds);
  }

  // 3. Para cada rango válido, encontrar los intervalos que cumplen con timeException y están dentro del rango.
  const validRanges = ranges.filter(isValidTimeRange);
  const resultIntervals = new Set<number>();

  validRanges.forEach((range) => {
    const [startHours, startMinutes] = range.start.split(':').map(Number);
    const [endHours, endMinutes] = (range.end === "00:00" ? "23:59" : range.end).split(':').map(Number);
    const rangeStartInSeconds = startHours * 3600 + startMinutes * 60;
    const rangeEndInSeconds = endHours * 3600 + endMinutes * 60;

    allPossibleIntervals.forEach((interval) => {
      if (interval >= rangeStartInSeconds && interval < rangeEndInSeconds) {
        // Entre interval y el tiempo final debe haber "timeException" segundos
        if (rangeEndInSeconds - interval >= timeException) {
          resultIntervals.add(interval);
        }
      }
    });
  });

  // 4. Convertir y ordenar los resultados
  return Array.from(resultIntervals)
    .sort((a, b) => a - b)
    .filter((seconds) => !isDate2Bigger(date, seconds, dateTimeLimit))
    .map(formatToAmPm);
}

export const getHours = (
  date: Date,
  agendaConfiguration: AgendaConfiguration,
  formattedBookings: DayWithHours[] = [],
  formattedGoogleEvents: DayWithHours[] = [],
  formatedRecurrenceDates: DayWithHours[] = []
) => {
  let availableHours: Hour[] = [];
  if (agendaConfiguration.specificDays && agendaConfiguration?.specificDays?.length > 0) {
    const index = agendaConfiguration.specificDays.findIndex(
      (item) => parseInt(item.date.substring(item.date.length - 2)) === date.getDate()
    );
    if (index > -1 && agendaConfiguration.specificDays[index].hours?.length > 0) {
      availableHours = [...agendaConfiguration.specificDays[index].hours];
    }
  }
  if (availableHours.length === 0) {
    const dayOfTheWeek = date.getDay();
    if (
      agendaConfiguration.weeklySchedule[weekDays[dayOfTheWeek] as keyof WeeklySchedule]?.length > 0
    ) {
      availableHours = [
        ...agendaConfiguration.weeklySchedule[weekDays[dayOfTheWeek] as keyof WeeklySchedule],
      ];
    }
  }
  let busyDays: DayWithHours[] =
    agendaConfiguration.busyDays && agendaConfiguration?.busyDays?.length > 0
      ? agendaConfiguration?.busyDays
      : [];
  if (formattedBookings.length > 0) {
    busyDays = [...busyDays, ...formattedBookings];
  }
  if (formattedGoogleEvents.length > 0) {
    busyDays = [...busyDays, ...formattedGoogleEvents];
  }
  if (formatedRecurrenceDates.length > 0) {
    busyDays = [...busyDays, ...formatedRecurrenceDates];
  }
  busyDays = busyDays.reduce((acc, current) => {
    const existingDay = acc.find((day) => day.date === current.date);

    if (existingDay) {
      existingDay.hours = mergeHours([...existingDay.hours, ...current.hours]);
    } else {
      acc.push(current);
    }

    return acc;
  }, [] as DayWithHours[]);

  if (busyDays.length > 0) {
    const index = busyDays.findIndex(
      (item) => {
        const [year, month, day] = item.date.split("-");
        return parseInt(year ?? "1800") === date.getFullYear() && parseInt(month ?? "13") === (date.getMonth()+1) && parseInt(day ?? "32") === date.getDate()
      }
    );
    if (index > -1 && busyDays[index].hours?.length > 0) {
      availableHours = getRemainingTimeSlots(availableHours, busyDays[index].hours);
    }
  }
  return generateTimeIntervals(
    availableHours,
    agendaConfiguration.startTimeIncrements,
    agendaConfiguration.sessionDuration + agendaConfiguration.timeAfterSessions,
    date,
    new Date(new Date().getTime() + agendaConfiguration.minimumNotice * 1000)
  );
};

export function secondsToTimeFormat(seconds: number): string {
  if (seconds < 0) {
    return '';
  }

  const hours = Math.floor(seconds / 3600);
  const remainingSeconds = seconds % 3600;
  const minutes = Math.floor(remainingSeconds / 60);
  const secs = remainingSeconds % 60;

  let result = '';
  if (hours > 0) result += `${hours}h `;
  if (minutes > 0) result += `${minutes}m `;
  if (secs > 0) result += `${secs}s`;

  return result.trim();
}

export function bookingsToDayWithHours(
  bookings: Booking[],
  timeBefore: number,
  timeAfter: number,
  timeZone: string
): DayWithHours[] {
  const groupedByDay: { [key: string]: Hour[] } = {};

  bookings.forEach((booking) => {
    const startDate = new Date(booking.startDate);

    const adjustedStartTime = toZonedTime(
      new Date(startDate.getTime() - timeBefore * 1000),
      timeZone
    );
    const adjustedEndTime = toZonedTime(
      new Date(startDate.getTime() + booking.duration * 1000 + timeAfter * 1000),
      timeZone
    );

    const dayKey = format(adjustedStartTime, 'yyyy-MM-dd', { timeZone });

    const start = format(adjustedStartTime, 'HH:mm', { timeZone });

    const endDayKey = format(adjustedEndTime, 'yyyy-MM-dd', { timeZone });
    let end = format(adjustedEndTime, 'HH:mm', { timeZone });
    if (end === "00:00" && endDayKey !== dayKey) {
        end = "00:00";
    }

    const hour: Hour = { start, end };

    if (!groupedByDay[dayKey]) {
      groupedByDay[dayKey] = [];
    }

    groupedByDay[dayKey].push(hour);
  });
  const resObj = Object.keys(groupedByDay).map((day) => {
    return {
      date: day,
      hours: mergeHours(groupedByDay[day]),
    }
  });

  return resObj;
}

export function googleEventsToDayWithHours(
  events: GoogleCalendarEvent[],
  timeBefore: number = 0,
  timeAfter: number = 0, 
  timeZone: string
): DayWithHours[] {
  const groupedByDay: { [key: string]: Hour[] } = {};

  events.forEach((event) => {
    if (event?.transparency === "transparent") return;

    let localStartDate: Date | undefined;
    let localEndDate: Date | undefined;

    if ('dateTime' in event.start && event.start.dateTime) {
      localStartDate = toZonedTime(new Date(event.start.dateTime).getTime() - timeBefore * 1000, timeZone);
    } 
    // else if ('date' in event.start && event.start.date) {
    //   localStartDate = toZonedTime(new Date(event.start.date + 'T00:00:00'), timeZone);
    // }

    if ('dateTime' in event.end && event.end.dateTime) {
      localEndDate = toZonedTime(new Date(event.end.dateTime).getTime() + timeAfter * 1000, timeZone);
    } 
    // else if ('date' in event.end && event.end.date) {
    //   const endDateAsDate = new Date(event.end.date + 'T00:00:00');
    //   localEndDate = toZonedTime(endDateAsDate.getTime() - 1, timeZone);
    // }

    if (!localStartDate || !localEndDate) {
      return;
    }

    const startDay = new Date(localStartDate.getFullYear(), localStartDate.getMonth(), localStartDate.getDate());
    const endDay = new Date(localEndDate.getFullYear(), localEndDate.getMonth(), localEndDate.getDate());

    const daysDifference = Math.floor((endDay.getTime() - startDay.getTime()) / (1000 * 60 * 60 * 24));

    for (let i = 0; i <= daysDifference; i++) {
      const currentDate = new Date(startDay);
      currentDate.setDate(startDay.getDate() + i);

      const dayKey = format(currentDate, 'yyyy-MM-dd', { timeZone });

      let segmentStart: Date;
      let segmentEnd: Date;

      if (i === 0) {
        segmentStart = localStartDate;
      } else {
        segmentStart = toZonedTime(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 0, 0, 0, 0), timeZone);
      }

      if (i === daysDifference) {
        segmentEnd = localEndDate;
      } else {
        segmentEnd = toZonedTime(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 23, 59, 59, 999), timeZone);
      }

      const startFormatted = format(segmentStart, 'HH:mm', { timeZone });
      let endFormatted = format(segmentEnd, 'HH:mm', { timeZone });

      const segmentEndDayKey = format(segmentEnd, 'yyyy-MM-dd', { timeZone });
      if (endFormatted === "00:00" && segmentEndDayKey !== dayKey) {
        endFormatted = "00:00";
      }


      const hour: Hour = { start: startFormatted, end: endFormatted };

      if (!groupedByDay[dayKey]) {
        groupedByDay[dayKey] = [];
      }
      groupedByDay[dayKey].push(hour);
    }
  });

  const resObj = Object.keys(groupedByDay).map((day) => ({
    date: day,
    hours: mergeHours(groupedByDay[day]),
  }));

  return resObj;
}

export function buildHoursofTheDaysOfTheMonthObject(
  month: number,
  startDateTime: Date,
  endDateTime: Date | null,
  agendaConfiguration: AgendaConfiguration,
  formattedBookings: DayWithHours[] = [],
  formattedGoogleEvents: DayWithHours[] = [],
  formatedRecurrenceDates: DayWithHours[] = [],
  forceAvailable: { [day: number]: string[] } = {}
): { [day: number]: string[] } {
  const year = startDateTime.getFullYear();
  const startDay = startDateTime.getMonth() === month ? startDateTime.getDate() : 1;
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
  const endDay = endDateTime?.getMonth() === month ? endDateTime.getDate() : lastDayOfMonth;

  const availableDays: { [day: number]: string[] } = {};

  for (let day = startDay; day <= endDay; day++) {
    const dateToSend = new Date(year, month, day, 0, 0, 0);
    availableDays[day] = getHours(
      dateToSend,
      agendaConfiguration,
      cloneDeep(formattedBookings),
      cloneDeep(formattedGoogleEvents),
      cloneDeep(formatedRecurrenceDates)
    );
  }

  const keys = Object.keys(forceAvailable);
  if (keys.length > 0) {
    for (let i = 0; i < keys.length; i++) {
      const numberKey = parseInt(keys[i]);
      if (numberKey) {
        if (availableDays[numberKey]) {
          availableDays[numberKey] = orderAmPmHours([
            ...availableDays[numberKey],
            ...forceAvailable[numberKey],
          ]);
        } else {
          availableDays[numberKey] = forceAvailable[numberKey];
        }
      }
    }
  }

  return availableDays;
}

export const mergeHours = (hours: Hour[]): Hour[] => {
  if (!hours || hours.length === 0) {
    return [];
  }
  const sortedHours = hours.sort((a, b) => parseTime(a.start) - parseTime(b.start));

  const merged: Hour[] = [];
  sortedHours.forEach((hour) => {
    if (merged.length === 0) {
      merged.push(hour);
    } else {
      const last = merged[merged.length - 1];
      if (
        parseTime(hour.start) <= parseTime(last.end === "00:00" ? "23:59" : last.end) ||
        parseTime(hour.start) <= parseTime(last.end === "00:00" ? "23:59" : last.end) + 4
      ) {
        last.end = formatTime(Math.max(parseTime(last.end === "00:00" ? "23:59" : last.end), parseTime(hour.end === "00:00" ? "23:59" : hour.end)));
      } else {
        merged.push(hour);
      }
    }
  });

  return merged;
};

export const convertTimezone = (
  day: DayWithHours,
  sourceTimezone: string,
  targetTimezone: string
): DayWithHours[] => {
  const convertedDays: DayWithHours[] = [];
  const DIFFERENCE_MINUTES = getTimeZoneDifference(sourceTimezone, targetTimezone);

  day.hours.forEach((hour) => {
    const startMinutes = parseTime(hour.start) + DIFFERENCE_MINUTES;
    const endMinutes = parseTime(hour.end === "00:00" ? "24:00" : hour.end) + DIFFERENCE_MINUTES;

    // Calcula el desplazamiento de días (positivo o negativo)
    const startDayOffset = Math.floor(startMinutes / (24 * 60));
    const endDayOffset = Math.floor((endMinutes - 1) / (24 * 60));

    // Normaliza los minutos dentro del día (0-1439)
    const normalizedStart = ((startMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
    const normalizedEnd = ((endMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);

    const baseDate = new Date(day.date);

    if (startDayOffset !== endDayOffset) {
      // Caso 1: El rango cruza el límite de día
      const startDate = modificarFecha(baseDate, startDayOffset, targetTimezone, DIFFERENCE_MINUTES > 0);
      const endDate = modificarFecha(baseDate, endDayOffset, targetTimezone, DIFFERENCE_MINUTES > 0);

      convertedDays.push(
        {
          date: startDate,
          hours: [{
            start: formatTime(normalizedStart),
            end: "00:00"
          }]
        },
        {
          date: endDate,
          hours: [{
            start: "00:00",
            end: formatTime(normalizedEnd)
          }]
        }
      );
    } else {
      // Caso 2: El rango está completo en un solo día
      const targetDate = modificarFecha(baseDate, startDayOffset, targetTimezone, DIFFERENCE_MINUTES > 0);
      const existingDay = convertedDays.find(d => d.date === targetDate);
      const newHour = {
        start: formatTime(normalizedStart),
        end: formatTime(normalizedEnd)
      };

      if (existingDay) {
        existingDay.hours.push(newHour);
      } else {
        convertedDays.push({
          date: targetDate,
          hours: [newHour]
        });
      }
    }
  });

  return convertedDays.map(day => ({
    date: day.date,
    hours: mergeHours(day.hours)
  }));
};

function modificarFecha(date: Date, dayOffset: number, timeZone: string, isPositive: boolean): string {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + dayOffset);
  if (isPositive) {
    return toZonedTime(newDate, timeZone).toISOString().split('T')[0];
  } else {
    return newDate.toISOString().split('T')[0];
  }
}

export const normalizeSpecificDays = (
  specificDays: DayWithHours[],
  sourceTimeZone: string,
  targetTimeZone: string
): DayWithHours[] => {
  const normalizedDays: Record<string, DayWithHours> = {};

  specificDays.forEach(day => {
    if (day.hours.length === 0) return;

    const convertedDays = convertTimezone(day, sourceTimeZone, targetTimeZone);
    convertedDays.forEach(convertedDay => {
      // Filtra horas inválidas (start >= end)
      const validHours = convertedDay.hours.filter(h => 
        parseTime(h.start) < parseTime(h.end === "00:00" ? "23:59" : h.end)
      );

      if (validHours.length > 0) {
        if (normalizedDays[convertedDay.date]) {
          normalizedDays[convertedDay.date].hours = mergeHours([
            ...normalizedDays[convertedDay.date].hours,
            ...validHours
          ]);
        } else {
          normalizedDays[convertedDay.date] = {
            date: convertedDay.date,
            hours: validHours
          };
        }
      }
    });
  });

  return Object.values(normalizedDays);
};

export function normalizeWeeklySchedule(
  weeklySchedule: WeeklySchedule,
  sourceTimeZone: string,
  targetTimeZone: string
): WeeklySchedule {
  const adjustedSchedule: Record<string, { start: string; end: string }[]> = {};
  const weekDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  // Procesamos los días en el weeklySchedule
  Object.entries(weeklySchedule).forEach(([day, ranges]) => {
    ranges.forEach((range) => {
      const today = startOfWeek(toZonedTime(new Date(), sourceTimeZone), {
        weekStartsOn: weekDays.indexOf(day) as Day,
      });

      // Normalizamos la franja horaria
      const normalized = normalizeTimeRange(
        today.toISOString(),
        range,
        sourceTimeZone,
        targetTimeZone
      );

      // Procesamos los rangos y los asignamos a los días correspondientes
      normalized.forEach((adjustedRange) => {
        const adjustedDay = format(parseISO(adjustedRange.date), 'EEEE').toLowerCase(); // Aseguramos que esté en minúsculas

        // Aseguramos que el día esté en la estructura del calendario ajustado
        if (!adjustedSchedule[adjustedDay]) {
          adjustedSchedule[adjustedDay] = [];
        }

        // Añadimos el rango ajustado a ese día
        adjustedSchedule[adjustedDay].push(...adjustedRange.ranges);
      });
    });
  });
  Object.entries(weeklySchedule).forEach(([day, ranges]) => {
    adjustedSchedule[day] = mergeHours(adjustedSchedule[day]);
  });

  // Aseguramos que todos los días de la semana estén presentes en el resultado
  weekDays.forEach((day) => {
    if (!adjustedSchedule[day]) {
      adjustedSchedule[day] = [];
    }
  });

  return adjustedSchedule as WeeklySchedule;
}

export function normalizeTimeRange(
  date: string,
  timeRange: { start: string; end: string },
  sourceTimeZone: string,
  targetTimeZone: string
): { date: string; ranges: { start: string; end: string }[] }[] {
  function parseDateOnUTC(date: string): Date {
    if (date.includes('T')) {
      // Caso donde la fecha ya tiene horas especificadas (e.g., 2025-01-24T06:00:00.000Z)
      return new Date(date);
    } else {
      // Caso donde solo se especifica la fecha (e.g., 2025-02-01)
      // Construimos el string en formato ISO con hora 00:00 en UTC
      const isoDate = `${date}T00:00:00.000Z`;
      return toZonedTime(date, targetTimeZone);
    }
  }
  let { start, end } = timeRange;

  if (!start || !end) {
    return [
      {
        date: format(new Date(date), 'yyyy-MM-dd'),
        ranges: [{ start: '', end: '' }],
      },
    ];
  }

  if (end === "00:00") {
    end = "24:00"
  }

  // Convertimos la fecha base y añadimos las horas del rango
  const dateStart = parseDateOnUTC(date);
  dateStart.setHours(parseInt(start.split(':')[0]), parseInt(start.split(':')[1]));

  const dateEnd = parseDateOnUTC(date);
  dateEnd.setHours(parseInt(end.split(':')[0]), parseInt(end.split(':')[1]));

  // Ajustamos el tiempo según la diferencia horaria
  const adjustedStart = new Date(dateStart);
  adjustedStart.setMinutes(
    adjustedStart.getMinutes() + getTimeZoneDifference(sourceTimeZone, targetTimeZone)
  );

  const adjustedEnd = new Date(dateEnd);
  adjustedEnd.setMinutes(
    adjustedEnd.getMinutes() + getTimeZoneDifference(sourceTimeZone, targetTimeZone)
  );

  const result: { date: string; ranges: { start: string; end: string }[] }[] = [];

  // Caso 1: adjustedStart y adjustedEnd están en el mismo día
  if (adjustedStart.toDateString() === adjustedEnd.toDateString()) {
    result.push({
      date: format(adjustedStart, 'yyyy-MM-dd'),
      ranges: [
        {
          start: format(adjustedStart, 'HH:mm'),
          end: format(adjustedEnd, 'HH:mm'),
        },
      ],
    });
  } else {
    // Caso 2: adjustedStart y adjustedEnd están en días diferentes

    result.push({
      date: format(adjustedStart, 'yyyy-MM-dd'),
      ranges: [
        {
          start: format(adjustedStart, 'HH:mm'),
          end: '00:00',
        },
      ],
    });

    if (format(adjustedEnd, 'HH:mm') !== '00:00') {
      // Rango del día de adjustedEnd desde 00:00
      result.push({
        date: format(adjustedEnd, 'yyyy-MM-dd'),
        ranges: [
          {
            start: '00:00',
            end: format(adjustedEnd, 'HH:mm'),
          },
        ],
      });
    }
  }

  return result;
}

export function getTimeZoneDifference(sourceTimeZone: string, targetTimeZone: string): number {
  const now = new Date();
    
  const offset1 = getTimezoneOffset(sourceTimeZone, now);
  const offset2 = getTimezoneOffset(targetTimeZone, now);
  return Math.floor((offset2-offset1) / (60 * 1000));
}
